import {
  StDropdown,
  StDropdownItem,
  StDropdownPortal,
  StDropdownSub,
  StDropdownSubContent,
  StDropdownSubTrigger,
  StDropdownTrigger,
} from "@storytell/ui/dropdown";
import { HiSolidArrowPath, HiSolidClock, HiSolidPlayCircle, HiSolidPlus } from "solid-icons/hi";
import { createSignal, For } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Dialog } from "@kobalte/core/dialog";
import { createStore } from "solid-js/store";
import { TbBrandGoogleDrive, TbBrandSlack, TbCalendar, TbFile, TbMail, TbMessage2, TbSlideshow } from "solid-icons/tb";

/**
 * Mock component for simulating creating a repeatable action from an operation
 */
export const OperationCreateAction = (props: { credits: number }) => {
  return (
    <Dialog>
      <Dialog.Trigger
        class={`group/btn
        flex items-center gap-2 rounded-xl px-2 py-0.5
        border border-emerald-600/30 dark:border-emerald-400/30
        bg-emerald-200/20 dark:bg-emerald-800/20 hover:bg-emerald-200/30 dark:hover:bg-emerald-800/30
        transition-colors cursor-pointer outline-none
        focus:ring-2 ring-emerald-600 dark:ring-emerald-300 opacity-60`}
      >
        <HiSolidPlayCircle class="size-5 text-emerald-700 dark:text-emerald-300s transition-transform group-hover/btn:scale-110 group-active/btn:scale-95" />

        <span class="font-medium text-xs text-emerald-900 dark:text-emerald-100">Create an Action</span>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay class="fixed inset-0 z-50 bg-black/50 dark:bg-white/10" />
        <div class="fixed z-50 inset-0 flex items-center justify-center text-on-surface dark:text-dark-on-surface">
          <Dialog.Content class="max-h-[80vh] w-screen max-w-xl overflow-y-auto rounded-xl bg-surface dark:bg-dark-surface p-8">
            <CreateActionPanel credits={props.credits} />
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog>
  );
};

const CreateActionPanel = (props: { credits: number }) => {
  const triggers = [
    { text: "On a schedule", icon: HiSolidClock },
    { text: "Whenever there is new source data", icon: HiSolidArrowPath },
  ];
  const destinations = [
    { text: "Send an SMS", icon: TbMessage2 },
    { text: "Send an Email", icon: TbMail },
    { text: "Create Calendar Item", icon: TbCalendar },
    { text: "Create Slides", icon: TbSlideshow },
    { text: "Create Document", icon: TbFile },
    { text: "Send to Slack", icon: TbBrandSlack },
    { text: "Add to GDrive", icon: TbBrandGoogleDrive },
  ];

  const [saved, setSaved] = createStore({
    activeTrigger: 0,
    scheduleText: null as string | null,
    destinationText: null as string | null,
    activeDestination: 0,

    triggerOpen: false,
  });
  const [period, setPeriod] = createSignal("Minute");
  const currentTime = () => new Date().toLocaleString("en-US", { hour: "numeric", minute: "numeric" });
  const currentDay = () => new Date().toLocaleString("en-US", { weekday: "long" });
  const currentDayInMonth = () => dayLabel(new Date().toLocaleString("en-US", { day: "numeric" }));
  const dayLabel = (day: string) => (day === "1" ? "1st" : day === "2" ? "2nd" : day === "3" ? "3rd" : `${day}th`);

  const textForPeriod = () => {
    switch (period()) {
      case "Day":
        return `Run every day at ${currentTime()}, Monday - Friday.`;
      case "Minute":
        return "Run every minute between 8am-5pm, Monday through Friday.";
      case "Hour":
        return "Run every hour between 8am-5pm, Monday through Friday.";
      case "Week":
        return `Run every week at ${currentTime()} on ${currentDay()}.`;
      case "Month":
        return `Run on the ${currentDayInMonth()} of every month at ${currentTime()}.`;
    }
  };

  return (
    <div>
      <h3 class="font-medium text-2xl mb-2">Turn this result into a repeatable Action</h3>
      {/* <p class="text mb-10"> */}
      {/*   This action will use{" "} */}
      {/*   <span class="text-lime-700 dark:text-lime-200 font-medium">{props.credits.toFixed(2)} credits</span> each time */}
      {/*   it runs. */}
      {/* </p> */}
      <p class="mb-10">(Coming Soon)</p>

      <div class="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-6 items-center text-lg">
        <span>Trigger</span>

        <div>
          <StDropdown
            open={saved.triggerOpen}
            onOpenChange={(open) => setSaved("triggerOpen", open)}
            contentProps={{ class: "outline-none z-50 px-1 py-1 flex flex-col gap-1" }}
            content={
              <>
                <StDropdownSub
                  onOpenChange={(open) => {
                    if (open) {
                      setTimeout(() => {
                        (document.getElementById("schedule-text") as HTMLTextAreaElement)?.focus();
                      }, 100);
                    }
                  }}
                  overlap
                  gutter={10}
                >
                  <StDropdownSubTrigger
                    class="flex items-center gap-2 text-lg rounded px-2 py-1
                    transition-colors cursor-pointer outline-none
                    focus:bg-black/10 dark:focus:bg-black/30"
                  >
                    <Dynamic component={triggers[0].icon} class="size-4" />
                    <span>{triggers[0].text}</span>
                  </StDropdownSubTrigger>
                  <StDropdownPortal>
                    <StDropdownSubContent
                      class={`hidden data-expanded:block z-50
                        animate-fade-in-quick min-w-md
                        text-sm p-2 origin-[var(--kb-menu-content-transform-origin)]
                        rounded-lg shadow-xl
                        bg-surface-lifted dark:bg-dark-surface-lifted
                        text-on-surface dark:text-dark-on-surface
                        border border-surface-decoration dark:border-dark-surface-decoration
                        `}
                    >
                      <div class="flex items-center justify-center gap-2 text-lg mb-5 mt-3">
                        <For each={["Minute", "Hour", "Day", "Week", "Month"]}>
                          {(item) => (
                            <button
                              type="button"
                              onClick={() => setPeriod(item)}
                              data-active={item === period()}
                              class="cursor-pointer px-2 py-1 rounded hover:bg-emerald-600/10 dark:hover:bg-emerald-600/10 data-[active=true]:bg-emerald-700 data-[active=true]:hover:bg-emerald-700 data-[active=true]:text-white"
                            >
                              {item}
                            </button>
                          )}
                        </For>
                      </div>

                      <textarea
                        rows={4}
                        id="schedule-text"
                        class="text-lg mb-2 p-2 block w-full resize-none outline-none rounded ring-2 ring-emerald-600"
                        value={saved.scheduleText ?? textForPeriod()}
                      />

                      <div class="flex justify-end">
                        <button
                          onClick={() => {
                            setSaved(
                              "scheduleText",
                              (document.getElementById("schedule-text") as HTMLTextAreaElement)?.value ??
                                textForPeriod(),
                            );
                            setSaved("activeTrigger", 0);
                            setSaved("triggerOpen", false);
                          }}
                          type="button"
                          class="bg-emerald-600 cursor-pointer text-white px-4 py-2 rounded-lg"
                        >
                          Update
                        </button>
                      </div>
                    </StDropdownSubContent>
                  </StDropdownPortal>
                </StDropdownSub>

                <StDropdownSub
                  overlap
                  gutter={10}
                  onOpenChange={(open) => {
                    if (open) {
                      setTimeout(() => {
                        (document.getElementById("destination-text") as HTMLTextAreaElement)?.focus();
                      }, 100);
                    }
                  }}
                >
                  <StDropdownSubTrigger
                    class="max-w-60 flex items-start gap-2 text-lg rounded px-2 py-1
                    transition-colors cursor-pointer outline-none
                    focus:bg-black/10 dark:focus:bg-black/30"
                  >
                    <Dynamic component={triggers[1].icon} class="size-4 mt-1" />
                    <span>{triggers[1].text}</span>
                  </StDropdownSubTrigger>
                  <StDropdownPortal>
                    <StDropdownSubContent
                      class={`hidden data-expanded:block z-50
                        animate-fade-in-quick min-w-md
                        text-sm p-2 origin-[var(--kb-menu-content-transform-origin)]
                        rounded-lg shadow-xl
                        bg-surface-lifted dark:bg-dark-surface-lifted
                        text-on-surface dark:text-dark-on-surface
                        border border-surface-decoration dark:border-dark-surface-decoration
                        `}
                    >
                      <textarea
                        rows={4}
                        id="destination-text"
                        class="text-lg p-2 mb-2 block w-full resize-none outline-none rounded ring-2 ring-emerald-600"
                        value={
                          saved.destinationText ?? "Run whenever there is new source data that matches what I uploaded."
                        }
                      />

                      <div class="flex justify-end">
                        <button
                          onClick={() => {
                            setSaved(
                              "destinationText",
                              (document.getElementById("destination-text") as HTMLTextAreaElement)?.value ??
                                "Run whenever there is new source data that matches what I uploaded.",
                            );
                            setSaved("activeTrigger", 1);
                            setSaved("triggerOpen", false);
                          }}
                          type="button"
                          class="bg-emerald-700 cursor-pointer text-white px-4 py-2 rounded-lg"
                        >
                          Update
                        </button>
                      </div>
                    </StDropdownSubContent>
                  </StDropdownPortal>
                </StDropdownSub>
              </>
            }
          >
            <StDropdownTrigger
              class="flex items-center gap-2 rounded-lg px-2 py-1 border border-surface-decoration dark:border-dark-surface-decoration
        transition-colors cursor-pointer outline-none
        focus:ring-2 ring-emerald-600 dark:ring-emerald-300"
            >
              <Dynamic component={triggers[saved.activeTrigger].icon} class="size-4" />
              <span>{triggers[saved.activeTrigger].text}</span>
            </StDropdownTrigger>
          </StDropdown>
        </div>

        <span>Destination</span>

        <div>
          <StDropdown
            contentProps={{ class: "outline-none z-50 px-1 py-1 flex flex-col gap-1" }}
            content={
              <>
                <For each={destinations}>
                  {(item, index) => (
                    <StDropdownItem
                      class="flex items-center gap-2 text-lg rounded px-2 py-1
                    transition-colors cursor-pointer outline-none
                    focus:bg-black/10 dark:focus:bg-black/30"
                      onClick={() => setSaved("activeDestination", index())}
                    >
                      <Dynamic component={item.icon} class="size-4" />
                      <span>{item.text}</span>
                    </StDropdownItem>
                  )}
                </For>

                <a
                  href="https://go.Storytell.ai/MCP"
                  target="_blank"
                  rel="noreferrer"
                  class="flex items-center gap-2 text-lg rounded px-2 py-1
                    transition-colors cursor-pointer outline-none text-primary dark:text-blue-300 underline
                    focus:bg-black/10 dark:focus:bg-black/30"
                >
                  <HiSolidPlus class="size-4" />
                  <span>+ Connect via MCP Server</span>
                </a>
                <a
                  href="https://roadmap.storytell.ai/?b=6849b614a27e78cbf0557010&sortBy=upvotes%3Adesc"
                  target="_blank"
                  rel="noreferrer"
                  class="flex items-center gap-2 text-lg rounded px-2 py-1
                    transition-colors cursor-pointer outline-none text-primary dark:text-blue-300 underline
                    focus:bg-black/10 dark:focus:bg-black/30"
                >
                  <HiSolidPlus class="size-4" />
                  <span>Suggest a new destination</span>
                </a>
              </>
            }
          >
            <StDropdownTrigger
              class="flex items-center gap-2 rounded-lg px-2 py-1 border border-surface-decoration dark:border-dark-surface-decoration
        transition-colors cursor-pointer outline-none
        focus:ring-2 ring-emerald-600 dark:ring-emerald-300"
            >
              <Dynamic component={destinations[saved.activeDestination].icon} class="size-4" />
              <span>{destinations[saved.activeDestination].text}</span>
            </StDropdownTrigger>
          </StDropdown>
        </div>
      </div>
      <div class="flex mt-5 justify-end">
        <Dialog.CloseButton
          disabled
          class="bg-emerald-700 opacity-60 cursor-pointer text-white flex items-center gap-2 px-4 py-2 rounded-lg"
        >
          <HiSolidPlayCircle />
          Create Action
        </Dialog.CloseButton>
      </div>
    </div>
  );
};
